import CheckGreen from "check-green.svg";
import Loader from "components/shared/loader";
import { clearLocalStorage } from "helpers/localStorage";
import { paths } from "helpers/paths";
import { getCrmUtmData } from "helpers/tracking";
import { useRouter } from "hooks/useRouter";
import React, { useEffect, useState } from "react";
import { alert } from "services/alert";
import { refreshToken } from "services/request";
import { registerOnlyOrganization } from "sources/account";
import { getOrganizationInfo } from "sources/atiSu";
import { Step1CompanyInfo, validateInn } from "./registration/step1CompanyInfo";

type OrganizationFormState = {
    name: string;
    inn: string;
    kpp: string;
};

const hasExistingOrganizationError = (message: string) => {
    const normalizedMessage = message.toLowerCase();

    return (
        /(?:инн|\binn\b).*(?:уже|занят|есть|exist|registered)/.test(normalizedMessage) ||
        /(?:организац|компан|organization|company).*(?:уже|exist|registered)/.test(normalizedMessage)
    );
};

export default function AtiSuOrganizationRegistration() {
    const router = useRouter();
    const [state, setState] = useState<OrganizationFormState>({ name: "", inn: "", kpp: "" });
    const [selectValue, setSelectValue] = useState<string | undefined>();
    const [manual, setManual] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showExistingOrganizationWarning, setShowExistingOrganizationWarning] = useState(false);

    const isFormValid = (state.name.length > 0 || state.inn.length > 0) && (manual ? validateInn(state.inn) : true);

    useEffect(() => {
        document.body.classList.add("registration-body-scroll");

        getOrganizationInfo()
            .then((organization) => {
                if (!organization) {
                    return;
                }

                setState({
                    name: organization.organizationName ?? "",
                    inn: organization.inn ?? "",
                    kpp: "",
                });
                setManual(true);
            })
            .catch(() => undefined);

        return () => document.body.classList.remove("registration-body-scroll");
    }, []);

    const handleSelect = (data: { value: string; option?: { name: string; inn: string; kpp: string } }) => {
        if (data.option) {
            setSelectValue(data.value);
            setState({ ...state, name: data.option.name, inn: data.option.inn, kpp: data.option.kpp ?? "" });
            return;
        }

        setSelectValue(undefined);
        setState({ ...state, name: "", inn: "", kpp: "" });
    };

    const returnTo = (path: string) => {
        clearLocalStorage();
        window.location.replace(path);
    };

    const getUtmData = async () => {
        const crmUtmData = await getCrmUtmData();

        return {
            ...crmUtmData,
            utmSource: router.query.utm_source,
            utmMedium: router.query.utm_medium,
            utmCampaign: router.query.utm_campaign,
            utmTerm: router.query.utm_term,
            utmContent: router.query.utm_content,
        };
    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (loading || !isFormValid) {
            return;
        }

        setLoading(true);

        try {
            const utmData = await getUtmData();
            const response = await registerOnlyOrganization({
                name: state.name,
                inn: state.inn,
                kpp: state.kpp || null,
                utmData,
            });

            if (response?.organizationId) {
                await refreshToken();
                window.location.replace(paths.registrationCreateRelay);
                return;
            }

            const errorText = response?.errorText || "Не удалось зарегистрировать организацию.";
            if (hasExistingOrganizationError(errorText)) {
                setShowExistingOrganizationWarning(true);
                return;
            }

            alert({ type: "error", message: errorText, title: "Ошибка." });
        } catch (_error) {
            alert({ type: "error", message: "Не удалось зарегистрировать организацию.", title: "Ошибка." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-page no-overflow h-100">
            <div className="identity-container">
                {loading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <div className="registration-container__shadow-top" />
                <div className="registration-container__shadow" />
                <div className="registration-container__background" />
                <div className="registration-container__header">
                    <div className="registration-container__header--logo" />
                </div>
                <div className="registration-panel__wrap">
                    <div className="registration-panel__title">
                        Почти готово!&nbsp;
                        <span className="font-size-52">
                            <CheckGreen />
                        </span>
                    </div>
                    <div className="registration-panel__subtitle">
                        Уточните данные организации, чтобы продолжить регистрацию
                    </div>
                    <div className="registration-panel__container">
                        <div className="registration-panel">
                            <form className="d-flex flex-column w-100" autoComplete="off" onSubmit={submit}>
                                <div className="registration-panel__inner--title">Данные организации</div>
                                <div className="registration-panel__fields">
                                    <div className="registration-panel__inner">
                                        <Step1CompanyInfo
                                            state={state}
                                            setState={setState}
                                            manual={manual}
                                            setManual={setManual}
                                            selectValue={selectValue}
                                            handleSelect={handleSelect}
                                            showEmail={false}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 mb-2 text-center registration-panel__inner">
                                    <button className="r-form__button" type="submit" disabled={loading || !isFormValid}>
                                        Продолжить
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {showExistingOrganizationWarning && (
                <>
                    <div className="r-relay-help__backdrop" />
                    <div
                        className="r-relay-help r-relay-help--center ati-su-organization-warning"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="ati-su-organization-warning-title"
                    >
                        <button
                            className="r-relay-help__close"
                            type="button"
                            aria-label="Вернуться к регистрации"
                            onClick={() => returnTo(paths.registration)}
                        >
                            ×
                        </button>
                        <div className="r-relay-help__title" id="ati-su-organization-warning-title">
                            Компания с таким ИНН уже зарегистрирована в системе.
                        </div>
                        <div className="r-relay-help__text">
                            Войдите под своей учетной записью или используйте другое юридическое лицо.
                        </div>
                        <button
                            className="r-form__button ati-su-organization-warning__login"
                            type="button"
                            onClick={() => returnTo(paths.login)}
                        >
                            Войти
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
